import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { IContentsDashboardDB, IDailyPerformanceDB } from 'type/db';

import BasicColumnChart from 'components/common/chart/BasicColumnChart';
import {
	useGetDashboardGroupSummary,
	useGetMissionPerformance,
} from 'hooks/queries/dashboard';
import useGetSearchParams from 'hooks/useGetSearchParams';
import CoreRadioChips from 'shared/chips/CoreRadioChips';
import CoreLoading from 'shared/CoreLoading';
import CoreSelect from 'shared/CoreSelect';
import { ReportSectionCard } from 'styles/styled-components/report';
import style from 'styles/styled-components/style';
import { groupOptionList, TGroupOption } from 'utils/date';

// mapper
const platform_report_mapper = {
	growth: [
		'종합 성과',
		'좋아요 수(릴스)',
		'좋아요 수(스타일클립)',
		'댓글 수',
		'조회 수',
		'발송 수(소셜비즈)',
		'클릭 수(소셜비즈)',
	],
};
// 데이터 맵핑
const dataKeyMapper: {
	[key in (typeof platform_report_mapper.growth)[number]]: keyof IDailyPerformanceDB;
} = {
	'종합 성과': 'totalPerformanceScore',
	'좋아요 수(릴스)': 'reelsLikeCount',
	'좋아요 수(스타일클립)': 'styleClipLikeCount',
	'댓글 수': 'commentCount',
	'조회 수': 'viewCount',
	'발송 수(소셜비즈)': 'sentCount',
	'클릭 수(소셜비즈)': 'clickCount',
};

function DailyTrendGraph() {
	const [groupId] = useGetSearchParams(['groupId']);

	const [selectedGraph, setSelectedGraph] = useState<string>(
		platform_report_mapper.growth[0],
	);
	const [selectedMission, setSelectedMission] = useState<string | undefined>(undefined);

	const {
		data: groupSummary,
		isLoading: groupSummaryLoading,
		isError: groupSummaryError,
	} = useGetDashboardGroupSummary(groupId);

	const missionList = useMemo(
		() =>
			groupSummary?.missionStatus.map((mission, index) => {
				return { label: `${index + 1}차 미션`, value: mission.missionNumber.toString() };
			}),
		[groupSummary],
	);

	const missionValue = missionList?.find((item) => item.label === selectedMission)?.value;

	const {
		data: missionPerformance,
		isLoading: missionPerformanceLoading,
		isError: missionPerformanceError,
	} = useGetMissionPerformance(groupId, missionValue);

	useEffect(() => {
		if (!missionList || missionList.length === 0) return;
		const lastMission = [...missionList].sort(
			(a, b) => Number(a.value) - Number(b.value),
		)[missionList.length - 1];
		setSelectedMission(lastMission?.label ?? '');
	}, [missionList]);

	const selectMissionChange = (value: string) => {
		if (groupSummary) {
			const mission = missionList?.find((item) => item.label === value)?.label;
			setSelectedMission(mission || '');
		}
	};

	const handleGraphChange = (value: string) => {
		setSelectedGraph(value);
	};

	const convertedContentResult = dataKeyMapper[selectedGraph];
	const selectedContentResult = missionPerformance?.[convertedContentResult];
	const selectedGraphData = selectedContentResult?.map((item: any) =>
		Math.trunc(item.value),
	);

	// 날짜 포맷 변경 (2023-12-01 -> 12-01)
	const categorys = selectedContentResult?.map((item: any) => {
		const [, month, day] = item.date.split('-');
		return `${month}-${day}`;
	});

	if (groupSummaryLoading && missionPerformanceLoading) {
		return (
			<>
				<ReportSectionCard>
					<Grid flex="center" _height="100%">
						<CoreLoading color_type="gray" />
					</Grid>
				</ReportSectionCard>
			</>
		);
	}
	return (
		<>
			<ReportSectionCard>
				<Grid flex="between">
					<CoreRadioChips
						group_type="carousel"
						style_type="default"
						_width="max-content"
						chips_list={platform_report_mapper.growth}
						selected_chips={selectedGraph}
						setSelectedChips={handleGraphChange}
					/>

					<CoreSelect
						_width="130px"
						_height="36px"
						select_type={'box'}
						option_list={missionList?.map((mission) => mission.label)}
						selected_option={selectedMission}
						setSelectedOption={selectMissionChange}
					/>
				</Grid>

				<BasicColumnChart
					height="268px"
					trend={1}
					zoom={false}
					data_name={'콘텐츠 성과'}
					xTickAmount={selectedContentResult?.length}
					yTickAmount={6}
					datas={selectedGraphData ?? []}
					categorys={categorys ?? []}
				/>
			</ReportSectionCard>
		</>
	);
}

const { Grid } = style;

export default DailyTrendGraph;
